import { put, takeLatest, select } from 'redux-saga/effects'
import * as Eff from 'redux-saga/effects' // tslint:disable-line:no-duplicate-imports
import axios from 'axios'
import toastr from 'toastr'

import Logger from '@Utils/Logger'
import router from '@Site/Router'

import {
  ISuccessResponse,
  IAction,
} from '@Root/types'

import {
  logoutSuccess,
  getUserInfoFailure,
  getUserInfoSuccess,
  createCustomerPortalSessionFailure,
  setLastUsedAccountSuccess,
  setLastUsedWorkspaceSuccess,
  resetShouldSeeNewsletterPopupSuccess,
  updateProfileSuccess,
  updateProfileFailure,
  changeAvatarSuccess,
  changeAvatarFailure
} from './actions'

import {
  finishSurvey,
} from '@SiteModules/Onboarding/actions'

import {
  IGetUserInfoSuccessResponseBody,
  UserActionTypes,
  IConnectExternalChannel,
  IOpenSsoAdminPortal,
  ISetLastViewedAlert,
  ILogoutPayload,
  ICreateCustomerPortalSessionPayload,
  IUpdateProfilePayload,
  IUpdateProfileFailurePayload,
  IChangeAvatarPayload,
} from './types'

import {
  ISetCurrentAccountPayload,
  AccountActionTypes,
} from '@SiteModules/Account/types'

import {
  ISetCurrentWorkspacePayload,
  WorkspaceActionTypes
} from '@SiteModules/Workspace/types'

import {
  PopupActionTypes,
} from '@SiteModules/Popup/types'

import {
  getLastUsedAccountId,
  getLastUsedWorkspaceId,
} from '@SiteModules/User/selectors'

import * as API from './api'

const call: any = Eff.call

function* logoutSaga(action: IAction<ILogoutPayload>) {
  try {
    yield call(API.logout)
    yield put(logoutSuccess())
    window.location.href = action.payload.forwardingUrl || '/users/sign_in'
    window.Checksub.token = window.Checksub.cookies.get('token')
  } catch (e) {
    Logger.error(e, 'Logout is failed')
  }
}

function* getUserInfoSaga() {
  try {
    const response: ISuccessResponse<IGetUserInfoSuccessResponseBody> = yield call(API.getUserInfo)
    const payload: IGetUserInfoSuccessResponseBody = response.data
    yield put(getUserInfoSuccess(payload))
    window.Checksub.token = window.Checksub.cookies.get('token')

    const { onboardingState } = payload.user.data.attributes
    const shouldRedirectToSurvey = onboardingState === 'survey'

    if (shouldRedirectToSurvey) {
      yield call(router.navigate, '/onboarding', { replace: true })
    }
  } catch (e) {
    Logger.error(e, 'User details cannot be loaded')
    yield put(getUserInfoFailure())
  }
}

interface IRedirectUri {
  readonly redirect_uri: string
}

function* connectExternalChannelSaga(action: IAction<IConnectExternalChannel>) {
  try {
    const response: ISuccessResponse<IRedirectUri> = yield call(API.connectExternalChannel, action.payload)
    window.location.href = response.data.redirect_uri
  } catch (e) {
    Logger.error(e, 'Authentication failed')
  }
}

function* createCustomerPortalSessionSaga(action: IAction<ICreateCustomerPortalSessionPayload>) {
  try {
    const response: ISuccessResponse<IRedirectUri> = yield call(API.createCustomerPortalSession, action.payload)
    window.location.href = response.data.redirect_uri
  } catch (e) {
    Logger.error(e, 'Can\'t open the billing')
    yield put(createCustomerPortalSessionFailure())
  }
}

function* openSsoAdminPortalSaga(action: IAction<IOpenSsoAdminPortal>) {
  try {
    const response: ISuccessResponse<IRedirectUri> = yield call(API.openSsoAdminPortal, action.payload)
    window.location.href = response.data.redirect_uri
  } catch (e) {
    Logger.error(e, 'Can\'t open the SSO admin portal')
  }
}

function* setLastViewedAlertSaga(action: IAction<ISetLastViewedAlert>) {
  try {
    yield call(API.setLastViewedAlert, action.payload)
  } catch (e) {
    Logger.error(e, 'Can\'t set last viewed alert')
  }
}

function* setLastUsedAccountSaga(action: IAction<ISetCurrentAccountPayload>): Generator<any, any, any> {
  try {
    const accountId = yield select(getLastUsedAccountId)

    if (action.payload.accountId < 0 || accountId === action.payload.accountId) { return }
    yield call(API.setLastUsedAccount, action.payload)
    yield put(setLastUsedAccountSuccess(action.payload))
  } catch (e) {
    Logger.error(e, 'Last used account cannot be saved')
  }
}

function* setLastUsedWorkspaceSaga(action: IAction<ISetCurrentWorkspacePayload>): Generator<any, any, any> {
  try {
    const workspaceId = yield select(getLastUsedWorkspaceId)

    if (action.payload.workspaceId < 0 || workspaceId === action.payload.workspaceId) { return }
    yield call(API.setLastUsedWorkspace, action.payload)
    yield put(setLastUsedWorkspaceSuccess(action.payload))
  } catch (e) {
    Logger.error(e, 'Last used workspace cannot be saved')
  }
}

function* openNewsletterPopupSaga() {
  try {
    yield call(API.resetShouldSeeNewsletterPopup)
    yield put(resetShouldSeeNewsletterPopupSuccess())
  } catch (e) {
    Logger.error(e, 'Resetting should_see_newsletter_popup failed.')
  }
}

function* updateProfileSaga(action: IAction<IUpdateProfilePayload>) {
  try {
    yield call(API.updateProfile, action.payload)
    yield put(updateProfileSuccess(action.payload))

    if (action.payload.shouldFinishSurvey) {
      yield put(finishSurvey())
    }
  } catch (e) {

    let payloadFailure: IUpdateProfileFailurePayload = {}
    const isInvalidPasswordError = axios.isAxiosError(e) && e.response && e.response.data.message === 'Current password is incorrect'
    if (isInvalidPasswordError && action.payload.email) {
      payloadFailure = { errorMessageCurrentPasswordForNewEmail: 'Invalid password' }
    } else if (isInvalidPasswordError && action.payload.password) {
      payloadFailure = { errorMessageCurrentPasswordForNewPassword: 'Invalid password' }
    } else {
      Logger.error(e, 'User profile cannot be updated')
    }

    yield put(updateProfileFailure(payloadFailure))
  }
}

function* changeAvatarSaga(action: IAction<IChangeAvatarPayload>) {
  try {
    yield call(API.changeAvatar, action.payload)
    yield put(changeAvatarSuccess(action.payload))
    toastr.success('', 'Image changed')
  } catch (e) {
    Logger.error(e, 'Image cannot be changed')
    yield put(changeAvatarFailure())
  }
}

function* userSagas() {
  yield takeLatest(UserActionTypes.LOGOUT, logoutSaga)
  yield takeLatest(UserActionTypes.GET_INFO, getUserInfoSaga)
  yield takeLatest(UserActionTypes.CREATE_CUSTOMER_PORTAL_SESSION, createCustomerPortalSessionSaga)
  yield takeLatest(UserActionTypes.CONNECT_EXTERNAL_CHANNEL, connectExternalChannelSaga)
  yield takeLatest(UserActionTypes.OPEN_SSO_ADMIN_PORTAL, openSsoAdminPortalSaga)
  yield takeLatest(UserActionTypes.SET_LAST_VIEWED_ALERT, setLastViewedAlertSaga)
  yield takeLatest(UserActionTypes.UPDATE_PROFILE, updateProfileSaga)
  yield takeLatest(UserActionTypes.CHANGE_AVATAR, changeAvatarSaga)
  yield takeLatest(AccountActionTypes.SET_CURRENT_ACCOUNT, setLastUsedAccountSaga)
  yield takeLatest(WorkspaceActionTypes.SET_CURRENT_WORKSPACE, setLastUsedWorkspaceSaga)
  yield takeLatest(PopupActionTypes.OPEN_NEWSLETTER_POPUP, openNewsletterPopupSaga)
}

export default userSagas
